/**
 * rebooking — client + types for same-price rebooking (BON-146):
 * `GET /api/v1/workshops/:id/rebook-targets` and
 * `POST /api/v2/bookings/:id/rebook`.
 *
 * The targets endpoint serves both the single-booking rebook (`bookingId` given)
 * and the workshop cancel, which moves every confirmed booking at once.
 *
 * The targets endpoint is the ONLY source of pickable destinations. The picker
 * must not fall back to `GET /workshops`: that route is READ-scoped, so it
 * would offer regions the caller cannot write to and the pick would only fail
 * at submit. Every row here is inside the caller's write breadth.
 *
 * Refusals are 409s discriminated by `code`, never by status alone — the refund
 * client had to be corrected once for collapsing three distinct 409s onto one
 * message, and this endpoint has nine.
 */
import { apiFetch } from '../lib/apiFetch';

/** Why a returned target cannot be chosen. `null` on a selectable row.
 *  Ineligible workshops are RETURNED rather than filtered out — a date that
 *  silently vanishes reads as a bug to the CSR looking for it. */
export type RebookReasonCode =
  | 'IS_SOURCE'
  | 'NO_CAPACITY'
  | 'PRICE_MISMATCH'
  | 'ENDED'
  | 'NOT_ACTIVE'
  /** In the eligibility union but not reachable from a listing, which is
   *  already confined to the booking's own region. Typed anyway so the union
   *  matches the server's rather than being a hopeful subset of it. */
  | 'REGION_MISMATCH';

/**
 * One destination row. Deliberately close to `api/workshops.ts#Workshop` so the
 * picker's helpers can serve both, but NOT identical — it nests venue/region/
 * species and names the local clock `localStartsAt`. Both shapes are normalized
 * onto `PickerWorkshop` (see `components/bookings/shared/pickerWorkshop.ts`)
 * rather than either one being cast to the other.
 *
 * `localStartsAt` is the venue-local wall clock as a NAIVE ISO string with no
 * trailing `Z` (the `Workshop.localStartDatetime` convention) — NOT the
 * offset-bearing form the workshop-cancel `RebookTarget` uses. Never parse it
 * as UTC.
 */
export interface RebookTargetRow {
  id: string;
  /** Absolute UTC instant. Sort/compare only — never displayed. */
  startsAt: string;
  /** Venue-local wall clock, naive, no `Z`. The only form displayed. Null when
   *  the workshop's local column is unpopulated — rare, but the server types it
   *  that way, so the picker falls back to the absolute instant. */
  localStartsAt: string | null;
  /** Absolute UTC instant for the end. Sort/compare only. */
  endsAt: string;
  priceCents: number;
  capacity: number;
  /** Hold-aware: capacity − confirmed − active holds. No second request. */
  seatsLeft: number;
  workshopTypeId: string;
  /** `name` is the non-null one; `displayName` is the optional override. Render
   *  `displayName ?? name`, the same precedence `WorkshopTypeSummary` uses. */
  workshopType: { name: string; displayName: string | null };
  treeSpecies: { id: string; name: string } | null;
  venue: { id: string; name: string; city: string; state: string };
  /** Null when the venue has no region on record. */
  region: { id: string; name: string } | null;
  selectable: boolean;
  reasonCode: RebookReasonCode | null;
  /** Server-supplied display copy for `reasonCode`. The frontend may override
   *  it — the picker does, so a disabled chip's reason matches the drawer's. */
  reasonLabel: string | null;
}

/** The `GET …/rebook-targets` envelope. Carries the cutoff verdict alongside
 *  the rows so the drawer never evaluates "10 AM at the venue" against the
 *  browser clock — that rule is the server's, and two implementations drift. */
export interface RebookTargetsEnvelope {
  /** ISO UTC. `null` when the venue's zone could not be resolved, in which
   *  case `cutoffPassed` is false — the cutoff fails OPEN, never closed. */
  cutoffAt: string | null;
  cutoffPassed: boolean;
  /** The caller holds `override:rebook-cutoff`. Distinct from `cutoffPassed`:
   *  the override affordance only appears when BOTH are true. */
  canOverrideCutoff: boolean;
  /** Seats that must fit in the destination, summed across every booking moving. */
  attendeeCount: number;
  sourceWorkshopId: string;
  /** Seeds the tree filter, so the common case is the tree they already booked
   *  and changing it is deliberate (decision 2). */
  sourceWorkshopTypeId: string;
  /** The server's 200-row cap was hit, so the month is not fully represented. */
  truncated: boolean;
  rows: RebookTargetRow[];
  /** Whole-workshop listings only: sales a workshop cancel must wait for. */
  pendingSales?: WorkshopPendingSales;
}

/** A checkout that has not settled: a CSR reservation holding seats, or a
 *  payment still in motion. */
export interface PendingSale {
  id: string;
  /** `manual_paid` for a CSR reservation. */
  origin: string;
  status: string;
  ticketCount: number;
  customerName: string | null;
}

export interface WorkshopPendingSales {
  liveReservations: PendingSale[];
  paymentsInFlight: PendingSale[];
}

/** `from` / `to` are ISO date strings (`yyyy-MM-dd`) bounding the fetch — the
 *  visible month ±1 week, matching what the create-booking picker already
 *  fetches. Omitting them lets the server pick its own window. */
export interface RebookTargetsQuery {
  /** Move only this booking. Omitted, every confirmed booking on the workshop moves. */
  bookingId?: string;
  from?: string;
  to?: string;
}

/** Where bookings on `workshopId` may move. Gated on `rebook:bookings` with a
 *  `bookingId`, on a workshop write without one. */
export async function fetchRebookTargets(
  workshopId: string,
  query: RebookTargetsQuery = {},
): Promise<RebookTargetsEnvelope> {
  const params = new URLSearchParams();
  if (query.bookingId) params.set('bookingId', query.bookingId);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  const qs = params.toString();
  const res = await apiFetch(`/api/v1/workshops/${workshopId}/rebook-targets${qs ? `?${qs}` : ''}`);
  if (!res.ok) throw await rebookErrorFromResponse(res, 'Could not load workshops to move to.');
  return (await res.json()) as RebookTargetsEnvelope;
}

export interface RebookBookingRequest {
  targetWorkshopId: string;
  reason: string;
  /** The CSR ticked the explicit override box. Never inferred from the cutoff
   *  state — the server derives `overrideUsed` as `requested && past cutoff`,
   *  so ticking it early is a harmless no-op rather than an error. */
  overrideCutoff: boolean;
}

/** Mirrors the frozen 200 envelope. `newBooking` is a full enough projection to
 *  render the receipt without a re-fetch (the refund envelope's booking is only
 *  `{id, status, amountPaidCents}`, which would not have been). */
export interface RebookBookingResult {
  /** The predecessor, as it now stands: zero cash, no payment reference. */
  booking: { id: string; status: 'REBOOKED'; amountPaidCents: number };
  newBooking: {
    id: string;
    /** Always present. Unlike an imported booking, a successor is minted by us
     *  and gets its own generated code, so the receipt can lead with it. */
    confirmationCode: string;
    workshopId: string;
    status: 'CONFIRMED';
    startsAt: string;
    /** Venue-local wall clock, naive, no `Z`. Null when the destination's local
     *  column is unpopulated. */
    localStartsAt: string | null;
    venueName: string;
    workshopName: string;
  };
  rebooking: {
    fromWorkshopId: string;
    toWorkshopId: string;
    reason: string;
    /** Server-DERIVED, not echoed from the request. */
    overrideUsed: boolean;
    cutoffAt: string | null;
    amountCents: number | null;
    createdAt: string;
  };
  /** False means the move committed but the customer was not told — the
   *  successor is flagged for review. Never a reason to report failure. */
  emailSent: boolean;
}

/** Machine-readable refusal codes. 409 unless noted. */
export type RebookErrorCode =
  | 'ALREADY_REBOOKED'
  | 'NOT_CONFIRMED'
  | 'NO_CAPACITY'
  | 'PAST_CUTOFF'
  | 'TARGET_NOT_BOOKABLE'
  | 'TARGET_IS_SOURCE'
  | 'TARGET_PRICE_MISMATCH'
  | 'TARGET_REGION_MISMATCH'
  | 'CONCURRENT_MODIFICATION'
  /** 403 — holds `rebook:bookings` but not `override:rebook-cutoff`. */
  | 'OVERRIDE_NOT_PERMITTED'
  | 'BOOKING_NOT_FOUND'
  | 'TARGET_NOT_FOUND'
  /** Targets listing only. 404. */
  | 'WORKSHOP_NOT_FOUND'
  /** Targets listing only — the booking is not on the workshop asked about. */
  | 'BOOKING_NOT_ON_WORKSHOP';

/** Extra fields some refusals carry, so the notice can be specific rather than
 *  generic. Every one of these exists to avoid a dead end: the successor's id
 *  turns "go find it yourself" into a link, and the real status turns "not
 *  confirmed" into "refunded". */
export interface RebookErrorDetail {
  /** ALREADY_REBOOKED — the successor. */
  successorBookingId?: string;
  successorConfirmationCode?: string | null;
  /** NOT_CONFIRMED — what the status ACTUALLY is. */
  bookingStatus?: string;
  /** NO_CAPACITY. */
  available?: number;
  requested?: number;
  /** 500 only — shown to the operator to hand to engineering. */
  requestId?: string;
}

export class RebookBookingError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: RebookErrorCode | undefined,
    readonly detail: RebookErrorDetail = {},
  ) {
    super(message);
    this.name = 'RebookBookingError';
  }
}

/** Default copy per code. Branch on the CODE before the status: nine refusals
 *  share 409, and two of them (`ALREADY_REBOOKED`, `NOT_CONFIRMED`) render as
 *  settled outcomes with links rather than as errors, so mistaking one for
 *  another sends the CSR somewhere that does not exist. */
const MESSAGE_BY_CODE: Record<RebookErrorCode, string> = {
  ALREADY_REBOOKED: 'This booking has already been moved to another workshop',
  NOT_CONFIRMED: 'Only a confirmed booking can be moved',
  NO_CAPACITY: 'That workshop no longer has room for this booking',
  PAST_CUTOFF: 'This booking is past the rebooking cutoff',
  TARGET_NOT_BOOKABLE: 'That workshop can no longer take bookings',
  TARGET_IS_SOURCE: 'That is the workshop this booking is already on',
  TARGET_PRICE_MISMATCH: 'That workshop is a different price',
  TARGET_REGION_MISMATCH: 'That workshop is in a different region',
  CONCURRENT_MODIFICATION: 'Someone else changed this booking — reload and try again',
  OVERRIDE_NOT_PERMITTED: 'You do not have permission to move a booking past the cutoff',
  BOOKING_NOT_FOUND: 'Booking not found',
  TARGET_NOT_FOUND: 'That workshop no longer exists',
  WORKSHOP_NOT_FOUND: 'This workshop no longer exists',
  BOOKING_NOT_ON_WORKSHOP: 'This booking is no longer on this workshop — reload and try again',
};

interface RebookErrorBody extends RebookErrorDetail {
  message?: string;
  code?: string;
}

function fallbackByStatus(status: number, generic: string): string {
  if (status === 403) return "You don't have permission to move this booking";
  if (status === 404) return 'Booking not found';
  if (status === 400) return 'Please check the workshop and reason, then try again';
  if (status === 409) return 'This booking cannot be moved';
  return generic;
}

async function rebookErrorFromResponse(
  res: Response,
  generic: string,
): Promise<RebookBookingError> {
  const body = (await res.json().catch(() => ({}))) as RebookErrorBody;
  const code = body.code as RebookErrorCode | undefined;
  const message =
    body.message ||
    (code && MESSAGE_BY_CODE[code]) ||
    fallbackByStatus(res.status, `${generic} (${res.status})`);
  return new RebookBookingError(message, res.status, code, {
    successorBookingId: body.successorBookingId,
    successorConfirmationCode: body.successorConfirmationCode,
    bookingStatus: body.bookingStatus,
    available: body.available,
    requested: body.requested,
    requestId: body.requestId,
  });
}

/** POST /api/v2/bookings/:id/rebook — gated server-side on `rebook:bookings`,
 *  and on `override:rebook-cutoff` too when the cutoff has passed. There is no
 *  confirmation step and no undo (decision 8), so the caller must guard against
 *  a double submit. */
export async function rebookBooking(
  bookingId: string,
  body: RebookBookingRequest,
): Promise<RebookBookingResult> {
  const res = await apiFetch(`/api/v2/bookings/${bookingId}/rebook`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await rebookErrorFromResponse(res, 'Could not move this booking.');
  return (await res.json()) as RebookBookingResult;
}
